import { t } from 'i18next';
import { SquareBadge, ThumbnailPhotoCard } from '@peacecover/component';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import { getPhotoCardBadges } from '../../trading/utils/getPhotoCardBadges';
import type { ReviewableTradingPhotoCardType } from '../types/ReviewableType';
import type { SelectedPhotoCard } from '../SelectTradePage';

interface ReviewTradingPhotoCardProps {
  tradingPhotoCard: ReviewableTradingPhotoCardType;
  reviewableId: string;
  setSelectedPhotoCard: (photoCard: SelectedPhotoCard) => void;
  setIsCompletedTradingPopupOpen: (isOpen: boolean) => void;
}

const ReviewTradingPhotoCard = ({
  tradingPhotoCard,
  reviewableId,
  setSelectedPhotoCard,
  setIsCompletedTradingPopupOpen
}: ReviewTradingPhotoCardProps) => {
  const badges = getPhotoCardBadges(tradingPhotoCard.photoCard);

  const handleSelectPhotoCard = () => {
    setSelectedPhotoCard({
      tradingPhotoCardId: tradingPhotoCard.tradingPhotoCardId,
      reviewerId: tradingPhotoCard.userProfile.userProfileId,
      reviewableId: reviewableId,
      reviewableType: 'TradingPhotoCard'
    });
    setIsCompletedTradingPopupOpen(true);
  };

  const hasBadges = badges.length > 0;

  return (
    <button
      type="button"
      onClick={handleSelectPhotoCard}
      className="rounded-[20px] border border-separator2 dark:border-separator1"
    >
      <div className="flex items-center px-[20px] py-[12px] bg-[rgba(0,0,0,0.02)] dark:bg-[rgba(255,255,255,0.02)]">
        <div className="flex items-center gap-[8px]">
          <ProfileAvatar
            thumbnailUrl={
              tradingPhotoCard.userProfile?.thumbnailUrl || tradingPhotoCard.userProfile?.pictureUrl
            }
            widthAndHeight="28px"
            svgWidthAndHeight="28px"
          />
          <p className="text-[15px] font-semibold text-text-main-lv1 max-w-[138px] truncate mr-[4px] leading-none">
            {tradingPhotoCard.userProfile.nickname}
          </p>
        </div>
        <SquareBadge
          styleVariant="solid"
          colorVariant="secondary"
          className="px-[5px] py-[3px] text-[12px]"
        >
          {t('SelectTradingPhotoCardReviewPage.category.sale')}
        </SquareBadge>
      </div>
      <div className="flex items-center gap-[8px] p-[20px] relative">
        <div className="w-[48px] h-[77px] flex-shrink-0">
          <ThumbnailPhotoCard
            imageUrl={
              tradingPhotoCard.photoCard.mediumThumbnailUrl || tradingPhotoCard.photoCard.pictureUrl
            }
            className="w-full h-full"
          />
        </div>
        <div className="w-full flex flex-col items-start gap-[8px] leading-none">
          <p className="text-[15px] font-semibold text-text-main-lv2 max-w-[200px] truncate">
            {t('SelectTradingPhotoCardReviewPage.tradingPhotoCardName', {
              nickname: tradingPhotoCard.photoCard.memberName,
              categoryName: t(
                `PhotoCardCategory.${tradingPhotoCard.photoCard.eventCategory}.${tradingPhotoCard.photoCard.category}.text`
              ),
              emoji: t(
                `PhotoCardCategory.${tradingPhotoCard.photoCard.eventCategory}.${tradingPhotoCard.photoCard.category}.emoji`
              )
            })}
          </p>
          <p className="text-[13px] font-medium text-text-main-lv2">
            {tradingPhotoCard.photoCard.eventName}
          </p>
          {hasBadges && (
            <div className="flex items-center justify-between w-full">
              <SquareBadge
                key={badges[0]}
                styleVariant="line"
                colorVariant="gray"
                className="px-[5px] py-[3px] text-text-main-lv4 max-w-[135px] truncate block"
              >
                {badges[0]}
              </SquareBadge>
              <p className="text-[16px] font-semibold text-primary-light dark:text-primary-dark">
                {tradingPhotoCard.price ?? t('SelectTradingPhotoCardReviewPage.offer')}
              </p>
            </div>
          )}
        </div>
        {!hasBadges && (
          <p className="absolute right-[20px] bottom-[23px] text-[16px] font-semibold text-primary-light dark:text-primary-dark">
            {tradingPhotoCard.price ?? t('SelectTradingPhotoCardReviewPage.offer')}
          </p>
        )}
      </div>
    </button>
  );
};

export default ReviewTradingPhotoCard;
